import { AfterViewInit, Component, ElementRef, Inject, Renderer2, ViewChild } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { AuthService } from '../../services/auth.service';

declare global {
    interface Window {
        GitBook: any;
        gitbookSettings: any;
    }
}

@Component({
    selector: 'app-gitbook-ai-embed',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './gitbook-ai-embed.component.html',
    styleUrl: './gitbook-ai-embed.component.scss',
})
export class GitbookAiEmbedComponent implements AfterViewInit {
    @ViewChild('embedContainer', { read: ElementRef }) embedContainer!: ElementRef;

    public isWidgetOpen = false;
    private isInitialized = false;

    constructor(
        private renderer: Renderer2,
        @Inject(DOCUMENT) private document: Document,
        private authService: AuthService,
    ) {}

    ngAfterViewInit(): void {
        this.loadGitbookScript();
    }

    public toggle(): void {
        if (window.GitBook) {
            if (this.isWidgetOpen) {
                this.closeChat();
            } else {
                // Se foi feito unload, precisamos reinicializar
                if (!this.isInitialized) {
                    this.initializeGitBook();
                } else {
                    window.GitBook('open');
                }

                this.hideGitbookLauncher();
                this.isWidgetOpen = true;
            }
        } else {
            console.warn('GitBook API not ready yet, trying to load...');
            this.loadGitbookScript();
        }
    }

    public closeChat(): void {
        if (window.GitBook) {
            window.GitBook('close');
            this.hideGitbookLauncher();
            this.isWidgetOpen = false;

            // Para limpar o chat, usamos o método clearChat
            setTimeout(() => {
                if (window.GitBook) {
                    try {
                        window.GitBook('clearChat');
                    } catch (e) {
                        console.error('Erro ao limpar chat GitBook:', e);
                    }
                }
            }, 300);
        }
    }

    private hideGitbookLauncher(): void {
        if (!window.GitBook) return;

        // Tenta esconder imediatamente
        window.GitBook('hide');

        // Tenta esconder novamente após alguns intervalos para garantir
        const attempts = [100, 500, 1000, 2000];
        attempts.forEach((timeout) => {
            setTimeout(() => {
                if (window.GitBook) {
                    window.GitBook('hide');
                }
            }, timeout);
        });
    }

    private getSiteUrl(): string {
        return this.authService.getHelpCenterUrl();
    }

    private initializeGitBook(): void {
        const siteUrl = this.getSiteUrl();

        window.gitbookSettings = {
            siteURL: siteUrl,
        };

        if (window.GitBook) {
            window.GitBook('init', window.gitbookSettings);
            this.isInitialized = true;
            this.hideGitbookLauncher();
        }
    }

    private loadGitbookScript(): void {
        const siteUrl = this.getSiteUrl();
        const scriptId = 'gitbook-ai-script';

        // Verifica se já foi carregado
        if (window.GitBook || document.getElementById(scriptId)) {
            if (window.GitBook) {
                this.initializeGitBook();
            }
            return;
        }

        // Script Loader
        const script = this.renderer.createElement('script');
        script.id = scriptId;

        // Tenta usar a URL base para o script se possível, mas mantendo a lógica original
        // O GitBook pode precisar do script vindo do domínio correto
        script.src = `${siteUrl}/~gitbook/embed/script.js`;

        script.async = true;
        script.defer = true;
        script.type = 'text/javascript';
        script.onload = () => {
            if (window.GitBook) {
                this.initializeGitBook();
                // Ocultar o botão padrão imediatamente após carregar
                setTimeout(() => window.GitBook('hide'), 250);
            }
        };

        // Injeta no body usando Renderer2
        this.renderer.appendChild(this.document.body, script);
    }
}
